import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  UserPlus,
  MessageCircle,
  CheckCircle2,
  LogOut,
  Trash2,
  RefreshCw,
  Loader2,
  ArrowLeft,
  ChevronDown,
  Search,
  Download,
  X,
  BarChart3,
  Table2,
  TrendingUp,
  MessageSquare,
  Send,
  CalendarDays,
  StickyNote,
  BookOpen,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axios from "axios";
import AdminCourses from "@/components/AdminCourses";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

/* ─── Stat Card ─── */
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={18} style={{ color }} strokeWidth={1.5} />
        </div>
        <span className="font-['Cormorant_Garamond'] text-3xl font-light text-[#1A1C19]">{value}</span>
      </div>
      <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#5C635A]">{label}</p>
    </div>
  );
}

/* ─── Status Badge ─── */
function StatusBadge({ status, onStatusChange, leadId }) {
  const [open, setOpen] = useState(false);
  const statuses = ["new", "contacted", "converted"];
  const badgeClass = status === "contacted" ? "badge-contacted" : status === "converted" ? "badge-converted" : "badge-new";

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className={`${badgeClass} cursor-pointer inline-flex items-center gap-1`} data-testid={`status-badge-${leadId}`}>
        {status}
        <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-[#EAE6DF] z-20 min-w-[120px] overflow-hidden">
          {statuses.map((s) => (
            <button key={s} onClick={() => { onStatusChange(leadId, s); setOpen(false); }} className="block w-full text-left px-4 py-2 text-xs uppercase tracking-wider text-[#5C635A] hover:bg-[#F7F5F0] transition-colors" data-testid={`status-option-${s}-${leadId}`}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Chart Tooltip ─── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/90 backdrop-blur-md border border-white/40 rounded-xl px-4 py-3 shadow-lg">
      <p className="text-xs font-medium text-[#5C635A] mb-1">{label}</p>
      <p className="text-sm font-medium text-[#1A1C19]">{payload[0].value} submission{payload[0].value !== 1 ? "s" : ""}</p>
    </div>
  );
}

function renderPieLabel({ name, percent }) {
  if (percent < 0.05) return null;
  return `${name} (${(percent * 100).toFixed(0)}%)`;
}

const PIE_COLORS = ["#4A6741", "#98A886", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];
const STATUS_COLORS = { new: "#4A6741", contacted: "#3b82f6", converted: "#10b981" };

/* ─── Notes Panel (slide-over) ─── */
function NotesPanel({ lead, onClose }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/leads/${lead.id}/notes`, { headers: getAuthHeaders() });
      setNotes(res.data.notes);
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  }, [lead.id]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/leads/${lead.id}/notes`, { text: newNote.trim() }, { headers: getAuthHeaders() });
      setNotes((prev) => [res.data, ...prev]);
      setNewNote("");
    } catch {
      // fail silently
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/admin/notes/${noteId}`, { headers: getAuthHeaders() });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch {
      // fail silently
    }
  };

  const formatNoteDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return dateStr;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex justify-end"
      data-testid="notes-panel-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="relative w-full max-w-md bg-[#F7F5F0] shadow-2xl flex flex-col h-full"
        data-testid="notes-panel"
      >
        {/* Panel Header */}
        <div className="px-6 py-5 border-b border-[#EAE6DF] bg-white/70 backdrop-blur-xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center">
                <StickyNote size={16} className="text-[#4A6741]" />
              </div>
              <div>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19]">Notes</h3>
                <p className="text-[11px] text-[#5C635A] font-light">{notes.length} note{notes.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#EAE6DF]/60 flex items-center justify-center text-[#5C635A] hover:text-[#1A1C19] transition-colors" data-testid="close-notes-panel">
              <X size={16} />
            </button>
          </div>

          {/* Lead Info Summary */}
          <div className="bg-[#F7F5F0] rounded-xl p-3">
            <p className="text-sm font-medium text-[#1A1C19]">{lead.name}</p>
            <p className="text-xs text-[#5C635A] font-light">{lead.email}{lead.phone ? ` · ${lead.phone}` : ""}</p>
            {lead.reason && <p className="text-xs text-[#5C635A] font-light mt-1 italic">"{lead.reason}"</p>}
          </div>
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[#4A6741]" />
            </div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="no-notes-message">
              <MessageSquare size={28} className="text-[#5C635A] opacity-20 mb-3" />
              <p className="text-sm font-light text-[#5C635A]">No notes yet</p>
              <p className="text-xs font-light text-[#5C635A] opacity-60 mt-1">Add a follow-up note below</p>
            </div>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="bg-white rounded-2xl p-4 group" data-testid={`note-${note.id}`}>
                <p className="text-sm font-light text-[#1A1C19] leading-relaxed whitespace-pre-wrap">{note.text}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] text-[#5C635A] font-light">{formatNoteDate(note.created_at)}</span>
                  <button
                    onClick={() => handleDeleteNote(note.id)}
                    className="opacity-0 group-hover:opacity-100 text-[#5C635A] hover:text-red-500 transition-all p-1"
                    data-testid={`delete-note-${note.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add Note Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 border-t border-[#EAE6DF] bg-white/70 backdrop-blur-xl">
          <div className="flex gap-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a follow-up note..."
              rows={2}
              className="flex-1 resize-none rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 px-4 py-3 text-sm font-light text-[#1A1C19] placeholder:text-[#5C635A]/50 outline-none transition-colors"
              data-testid="note-input"
            />
            <button
              type="submit"
              disabled={submitting || !newNote.trim()}
              className="self-end w-10 h-10 rounded-xl bg-[#4A6741] text-white flex items-center justify-center hover:bg-[#2C4027] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="submit-note-button"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

/* ─── Date Range Presets ─── */
const DATE_PRESETS = [
  { label: "All Time", value: "all" },
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "Last 90 Days", value: "90d" },
  { label: "Custom", value: "custom" },
];

function getDateRange(preset) {
  const now = new Date();
  const todayStr = now.toISOString().split("T")[0];
  switch (preset) {
    case "today":
      return { from: todayStr, to: todayStr };
    case "7d": {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return { from: d.toISOString().split("T")[0], to: todayStr };
    }
    case "30d": {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return { from: d.toISOString().split("T")[0], to: todayStr };
    }
    case "90d": {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return { from: d.toISOString().split("T")[0], to: todayStr };
    }
    default:
      return { from: "", to: "" };
  }
}

/* ─── Main Dashboard ─── */
export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [analytics, setAnalytics] = useState({ trend: [], sources: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("leads");

  // Search & Filter
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [exporting, setExporting] = useState(false);

  // Date Range
  const [datePreset, setDatePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // Notes Panel
  const [selectedLead, setSelectedLead] = useState(null);

  const navigate = useNavigate();

  // Compute actual date range from preset or custom
  const dateRange = useMemo(() => {
    if (datePreset === "custom") return { from: customFrom, to: customTo };
    if (datePreset === "all") return { from: "", to: "" };
    return getDateRange(datePreset);
  }, [datePreset, customFrom, customTo]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterSource !== "all") params.source = filterSource;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;

      const [leadsRes, statsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers, params }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers }),
      ]);
      setLeads(leadsRes.data.leads);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setError("");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        setError("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, searchQuery, filterStatus, filterSource, dateRange]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return; }
    fetchData();
  }, [navigate, fetchData]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const uniqueSources = useMemo(() => {
    const sources = new Set(leads.map((l) => l.referral_source).filter(Boolean));
    return Array.from(sources);
  }, [leads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, { status: newStatus }, { headers: getAuthHeaders() });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
      const statsRes = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(statsRes.data);
    } catch {
      setError("Failed to update status");
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, { headers: getAuthHeaders() });
      if (selectedLead?.id === leadId) setSelectedLead(null);
      fetchData();
    } catch {
      setError("Failed to delete lead");
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/leads/export/csv`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "align_leads_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = () => { localStorage.removeItem("admin_token"); navigate("/admin"); };

  const clearFilters = () => {
    setSearchInput(""); setSearchQuery(""); setFilterStatus("all"); setFilterSource("all");
    setDatePreset("all"); setCustomFrom(""); setCustomTo("");
  };

  const hasActiveFilters = searchInput || filterStatus !== "all" || filterSource !== "all" || datePreset !== "all";

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return dateStr; }
  };

  const formatChartDate = (dateStr) => {
    try { const d = new Date(dateStr + "T00:00:00"); return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" }); }
    catch { return dateStr; }
  };

  return (
    <>
      <Helmet>
        <title>Dashboard | ALIGN&bull; Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: "#F7F5F0" }} data-testid="admin-dashboard">
        {/* Header */}
        <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="text-[#5C635A] hover:text-[#4A6741] transition-colors" data-testid="dashboard-back-button">
                <ArrowLeft size={18} />
              </button>
              <h1 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19]">
                ALIGN<span className="text-[#4A6741]">&bull;</span>{" "}
                <span className="text-[#5C635A] font-['Manrope'] text-xs uppercase tracking-widest font-medium">Dashboard</span>
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center bg-[#EAE6DF]/60 rounded-full p-1">
                <button onClick={() => setActiveTab("leads")} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all ${activeTab === "leads" ? "bg-white text-[#1A1C19] shadow-sm" : "text-[#5C635A] hover:text-[#1A1C19]"}`} data-testid="tab-leads">
                  <Table2 size={12} /> Leads
                </button>
                <button onClick={() => setActiveTab("analytics")} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all ${activeTab === "analytics" ? "bg-white text-[#1A1C19] shadow-sm" : "text-[#5C635A] hover:text-[#1A1C19]"}`} data-testid="tab-analytics">
                  <BarChart3 size={12} /> Analytics
                </button>
                <button onClick={() => setActiveTab("courses")} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all ${activeTab === "courses" ? "bg-white text-[#1A1C19] shadow-sm" : "text-[#5C635A] hover:text-[#1A1C19]"}`} data-testid="tab-courses">
                  <BookOpen size={12} /> Courses
                </button>
              </div>
              <button onClick={fetchData} className="w-9 h-9 rounded-full bg-[#4A6741]/10 flex items-center justify-center text-[#4A6741] hover:bg-[#4A6741]/20 transition-colors" data-testid="refresh-button">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
              <button onClick={handleLogout} className="btn-pill-outline text-[10px] py-2 px-5" data-testid="logout-button">
                <LogOut size={12} /> Sign Out
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          {error && (
            <div className="mb-6 p-4 rounded-2xl bg-red-50 text-red-700 text-sm font-light" data-testid="dashboard-error">{error}</div>
          )}

          {/* Mobile Tab */}
          <div className="flex md:hidden items-center bg-[#EAE6DF]/60 rounded-full p-1 mb-6">
            <button onClick={() => setActiveTab("leads")} className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all ${activeTab === "leads" ? "bg-white text-[#1A1C19] shadow-sm" : "text-[#5C635A]"}`}>
              <Table2 size={12} /> Leads
            </button>
            <button onClick={() => setActiveTab("analytics")} className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all ${activeTab === "analytics" ? "bg-white text-[#1A1C19] shadow-sm" : "text-[#5C635A]"}`}>
              <BarChart3 size={12} /> Analytics
            </button>
            <button onClick={() => setActiveTab("courses")} className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all ${activeTab === "courses" ? "bg-white text-[#1A1C19] shadow-sm" : "text-[#5C635A]"}`}>
              <BookOpen size={12} /> Courses
            </button>
          </div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Leads" value={stats.total} color="#4A6741" />
            <StatCard icon={UserPlus} label="New" value={stats.new} color="#4A6741" />
            <StatCard icon={MessageCircle} label="Contacted" value={stats.contacted} color="#3b82f6" />
            <StatCard icon={CheckCircle2} label="Converted" value={stats.converted} color="#10b981" />
          </motion.div>

          {/* ─── LEADS TAB ─── */}
          {activeTab === "leads" && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="glass-card overflow-hidden">
              {/* Search / Filter / Export */}
              <div className="p-6 border-b border-[#EAE6DF]/50">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <h2 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1A1C19]">Expressions of Interest</h2>
                    <p className="text-xs text-[#5C635A] font-light mt-1">{leads.length} result{leads.length !== 1 ? "s" : ""}{hasActiveFilters ? " (filtered)" : ""}</p>
                  </div>
                  <button onClick={handleExportCSV} disabled={exporting} className="btn-pill text-[10px] py-2.5 px-5 inline-flex items-center gap-2 self-start" data-testid="export-csv-button">
                    {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Export CSV
                  </button>
                </div>

                {/* Row 1: Search + Status + Source */}
                <div className="flex flex-col md:flex-row gap-3 mb-3">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C635A]" />
                    <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by name, email, phone, or reason..." className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] placeholder:text-[#5C635A]/50 outline-none transition-colors" data-testid="search-input" />
                    {searchInput && (
                      <button onClick={() => setSearchInput("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C635A] hover:text-[#1A1C19]"><X size={14} /></button>
                    )}
                  </div>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none cursor-pointer appearance-none min-w-[140px]" data-testid="filter-status">
                    <option value="all">All Statuses</option>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                  </select>
                  <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none cursor-pointer appearance-none min-w-[160px]" data-testid="filter-source">
                    <option value="all">All Sources</option>
                    {uniqueSources.map((src) => (<option key={src} value={src}>{src}</option>))}
                  </select>
                </div>

                {/* Row 2: Date Range */}
                <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                  <div className="flex items-center gap-2">
                    <CalendarDays size={14} className="text-[#5C635A] shrink-0" />
                    <div className="flex flex-wrap gap-1.5">
                      {DATE_PRESETS.map((p) => (
                        <button
                          key={p.value}
                          onClick={() => setDatePreset(p.value)}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${datePreset === p.value ? "bg-[#4A6741] text-white" : "bg-[#F7F5F0] text-[#5C635A] hover:bg-[#EAE6DF]"}`}
                          data-testid={`date-preset-${p.value}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Date Inputs */}
                  {datePreset === "custom" && (
                    <div className="flex items-center gap-2">
                      <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-3 py-1.5 rounded-lg bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-xs font-light text-[#1A1C19] outline-none" data-testid="custom-date-from" />
                      <span className="text-xs text-[#5C635A]">to</span>
                      <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-3 py-1.5 rounded-lg bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-xs font-light text-[#1A1C19] outline-none" data-testid="custom-date-to" />
                    </div>
                  )}

                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="px-4 py-1.5 rounded-lg text-xs uppercase tracking-wider font-medium text-[#4A6741] hover:bg-[#4A6741]/10 transition-colors whitespace-nowrap" data-testid="clear-filters-button">
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              {loading ? (
                <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
                  <Loader2 size={24} className="animate-spin text-[#4A6741]" />
                </div>
              ) : leads.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="no-leads-message">
                  <Users size={32} className="text-[#5C635A] opacity-30 mb-4" />
                  <p className="text-sm font-light text-[#5C635A]">{hasActiveFilters ? "No leads match your filters" : "No expressions of interest yet"}</p>
                  {hasActiveFilters && <button onClick={clearFilters} className="mt-3 text-xs text-[#4A6741] underline">Clear filters</button>}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full admin-table" data-testid="leads-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Reason</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Notes</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                          <td className="font-medium text-[#1A1C19]">{lead.name}</td>
                          <td>{lead.email}</td>
                          <td>{lead.phone || "—"}</td>
                          <td><span className="max-w-[200px] truncate block" title={lead.reason}>{lead.reason || "—"}</span></td>
                          <td>{lead.referral_source || "—"}</td>
                          <td><StatusBadge status={lead.status} onStatusChange={handleStatusChange} leadId={lead.id} /></td>
                          <td className="text-xs text-[#5C635A]">{formatDate(lead.created_at)}</td>
                          <td>
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="w-8 h-8 rounded-lg bg-[#4A6741]/5 hover:bg-[#4A6741]/15 flex items-center justify-center text-[#4A6741] transition-colors"
                              data-testid={`open-notes-${lead.id}`}
                            >
                              <MessageSquare size={14} />
                            </button>
                          </td>
                          <td>
                            <button onClick={() => handleDelete(lead.id)} className="text-[#5C635A] hover:text-red-500 transition-colors p-2" data-testid={`delete-lead-${lead.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {/* ─── ANALYTICS TAB ─── */}
          {activeTab === "analytics" && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="space-y-6" data-testid="analytics-section">
              {/* Trend Chart */}
              <div className="glass-card p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center"><TrendingUp size={16} className="text-[#4A6741]" /></div>
                  <div>
                    <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19]">Submission Trend</h3>
                    <p className="text-xs text-[#5C635A] font-light">Last 30 days</p>
                  </div>
                </div>
                <div className="h-[280px]" data-testid="trend-chart">
                  {analytics.trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analytics.trend}>
                        <defs><linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4A6741" stopOpacity={0.2} /><stop offset="95%" stopColor="#4A6741" stopOpacity={0} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" />
                        <XAxis dataKey="date" tickFormatter={formatChartDate} tick={{ fontSize: 11, fill: "#5C635A", fontFamily: "Manrope" }} axisLine={{ stroke: "#EAE6DF" }} tickLine={false} interval="preserveStartEnd" />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#5C635A", fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Area type="monotone" dataKey="count" stroke="#4A6741" strokeWidth={2} fill="url(#colorCount)" dot={false} activeDot={{ r: 5, fill: "#4A6741", strokeWidth: 0 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-[#5C635A] font-light">No data available</div>
                  )}
                </div>
              </div>

              {/* Pie Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass-card p-6 md:p-8">
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19] mb-6">Referral Sources</h3>
                  <div className="h-[260px]" data-testid="sources-chart">
                    {analytics.sources.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart><Pie data={analytics.sources} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" label={renderPieLabel}>{analytics.sources.map((entry, index) => (<Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />))}</Pie><Tooltip contentStyle={{ background: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: "12px", fontSize: "13px", fontFamily: "Manrope" }} /></PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-sm text-[#5C635A] font-light">No data available</div>
                    )}
                  </div>
                </div>
                <div className="glass-card p-6 md:p-8">
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19] mb-6">Lead Status</h3>
                  <div className="h-[260px]" data-testid="status-chart">
                    {analytics.statuses.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart><Pie data={analytics.statuses} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" label={renderPieLabel}>{analytics.statuses.map((entry) => (<Cell key={`cell-${entry.name}`} fill={STATUS_COLORS[entry.name] || "#98A886"} />))}</Pie><Tooltip contentStyle={{ background: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: "12px", fontSize: "13px", fontFamily: "Manrope" }} /></PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-sm text-[#5C635A] font-light">No data available</div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─── COURSES TAB ─── */}
          {activeTab === "courses" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-card p-6 md:p-8"
            >
              <AdminCourses />
            </motion.div>
          )}
        </main>
      </div>

      {/* Notes Panel Slide-Over */}
      <AnimatePresence>
        {selectedLead && <NotesPanel lead={selectedLead} onClose={() => setSelectedLead(null)} />}
      </AnimatePresence>
    </>
  );
}
